import { useNavigate } from "@tanstack/react-router"
import { sectionByPath } from "../../shell/nav-items"
import { firstAllowedPath } from "./section-routes"
import type { ReactNode } from "react"

import { evaluateSection } from "../../access/access-state"
import { useAccessState } from "../../access/use-access"
import type { KnownPermission } from "../../access/permissions"
import type { NavSectionId } from "../../shell/nav-items"
import { sectionById } from "../../shell/nav-items"
import { AppLoading, DeniedScreen } from "../../shell/states"
import { PlaceholderScreen } from "../../screens/placeholder/placeholder-screen"

/**
 * Защита раздела правом.
 *
 * Это КОМПОНЕНТ, а не проверка в beforeLoad с перенаправлением, и так сделано намеренно:
 * отказ показывается по адресу самого раздела. Кнопка «назад» работает, адрес называет
 * раздел, ссылку можно переслать владельцу со словами «сюда меня не пускает».
 * Перенаправление на общий экран отказа стирает всё это сразу.
 *
 * Решение о показе принимает evaluateSection — чистая функция, у которой «нет доступа»
 * возвращается ТОЛЬКО из состояния «профиль загружен».
 */
export function SectionGuard(props: {
  readonly id: NavSectionId
  readonly children: ReactNode
  /**
   * Право подраздела, если оно СТРОЖЕ права раздела.
   *
   * ЗАЧЕМ. Экран шаблонов сообщений лежит в настройках, и страж пускал в него по праву
   * раздела — `settings.view`. Сервер при этом требует `templates.manage`, и человек
   * с одним правом видел пустой экран с отказами от каждого запроса вместо внятного
   * «этого права у вас нет». Обратный случай не менее важен: у кого есть
   * `templates.manage`, но нет `settings.view`, экран открывать надо — иначе право
   * выдано, а воспользоваться им нечем.
   */
  readonly permission?: KnownPermission
}): ReactNode {
  const section = sectionById(props.id)
  const state = useAccessState()
  const access = evaluateSection(state, props.permission ?? section.permission)
  const navigate = useNavigate()

  switch (access.kind) {
    case "allowed":
      return props.children
    case "denied":
      return (
        <DeniedScreen
          sectionLabel={section.label}
          required={access.required}
          viewerRole={state.kind === "ready" ? state.context.role.code : null}
          viewerRoleName={state.kind === "ready" ? state.context.role.name : "своя роль салона"}
          homeLabel={sectionByPath(firstAllowedPath(state))?.label ?? "Журнал"}
          onHome={() => {
            // Тупик без выхода человек закрывает вкладкой. Ведём в ПЕРВЫЙ ОТКРЫТЫЙ
            // раздел, а не жёстко в журнал: у бухгалтера нет schedule.view, и кнопка
            // «В журнал» приводила бы его ко второму такому же отказу.
            void navigate({ to: firstAllowedPath(state) })
          }}
        />
      )
    default:
      // «Проверяем доступ», «сессия кончилась», «сбой» — всё это уже обработано
      // защищённым маршрутом выше. Сюда попадают только доли секунды между состояниями,
      // и в них показывается каркас, а не отказ.
      return <AppLoading />
  }
}

/** Раздел-заглушка: защита правом плюс честное «ещё не сделано». */
export function SectionStub(props: {
  readonly id: NavSectionId
  readonly summary: string
}): ReactNode {
  const section = sectionById(props.id)
  return (
    <SectionGuard id={props.id}>
      <PlaceholderScreen id={props.id} label={section.label} summary={props.summary} />
    </SectionGuard>
  )
}
